// apps/admin-ui/src/pages/DexOnRampPage.tsx
// DEX On-Ramp (Crypto Funding) management page
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-09: Full visual polish — migrated from raw CSS to Tailwind
// design system matching OrdersPage, GovernancePage, and admin-ui Layout.
// FIXED 2026-02-09: Was using api.get() from client.ts which routes through
// the ledger service (port 3030). DEX on-ramp endpoints live on the exchange
// service (port 3009). Now uses useDexOnRampOrders hook from useExchangeAdmin.

import { useState, useCallback } from 'react';
import { useDexOnRampStats, useDexOnRampOrders } from '../hooks/useExchangeAdmin';

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatCurrency(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(num);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncateHash(hash: string): string {
  if (!hash) return '—';
  return `${hash.slice(0, 10)}…${hash.slice(-6)}`;
}

function getStatusClasses(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-success-subtle text-success-emphasis';
    case 'failed':
    case 'mint_failed':
      return 'bg-error-subtle text-error-emphasis';
    case 'pending':
    case 'submitted':
    case 'confirming':
    case 'crediting':
      return 'bg-warning-subtle text-warning-emphasis';
    default:
      return 'bg-surface-emphasis text-content-muted';
  }
}

function getStatusDot(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-success';
    case 'failed':
    case 'mint_failed':
      return 'bg-error';
    case 'pending':
    case 'submitted':
    case 'confirming':
    case 'crediting':
      return 'bg-warning';
    default:
      return 'bg-content-muted';
  }
}

function getEtherscanUrl(txHash: string, networkMode: string): string {
  return networkMode === 'testnet'
    ? `https://sepolia.etherscan.io/tx/${txHash}`
    : `https://etherscan.io/tx/${txHash}`;
}

// ═══════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'success' | 'warning' | 'error' | 'info' | 'default';
}) {
  const borderColor = {
    success: 'border-t-success',
    warning: 'border-t-warning',
    error:   'border-t-error',
    info:    'border-t-info',
    default: 'border-t-brand-500',
  }[accent || 'default'];

  return (
    <div className={`bg-surface rounded-xl border border-edge shadow-sm ${borderColor} border-t-[3px] p-5`}>
      <p className="text-2xs uppercase tracking-wider font-medium text-content-muted mb-1">{label}</p>
      <p className="text-2xl font-bold text-content">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const label = status.replace(/_/g, ' ');
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusClasses(status)}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${getStatusDot(status)}`} />
      {label}
    </span>
  );
}

function NetworkBadge({ mode }: { mode: 'mainnet' | 'testnet' }) {
  const classes = mode === 'testnet'
    ? 'bg-warning-subtle text-warning-emphasis'
    : 'bg-success-subtle text-success-emphasis';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide ${classes}`}>
      {mode}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-full bg-surface-muted flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
        </svg>
      </div>
      <h3 className="text-base font-semibold text-content mb-1">No on-ramp orders found</h3>
      <p className="text-sm text-content-secondary">Try adjusting your filters or check back later.</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export function DexOnRampPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [networkFilter, setNetworkFilter] = useState('');

  // Stats from the exchange service
  const { stats } = useDexOnRampStats();

  // Orders from the exchange service (NOT the ledger client)
  const { orders, total, totalPages, isLoading, error, refetch } = useDexOnRampOrders({
    status: statusFilter || undefined,
    networkMode: networkFilter || undefined,
    page,
    pageSize,
  });

  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setNetworkFilter('');
    setPage(1);
  }, []);

  const hasActiveFilters = statusFilter !== '' || networkFilter !== '';

  return (
    <div className="p-6 max-w-[1440px] mx-auto">
      {/* ─── Page Header ─── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-content">DEX On-Ramp Orders</h1>
          <p className="text-sm text-content-secondary mt-0.5">
            Crypto funding transactions via DEX integration
          </p>
        </div>
        <button
          onClick={refetch}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* ─── Summary Cards ─── */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            label="Processing"
            value={String((stats.pending || 0) + (stats.submitted || 0))}
            accent="warning"
          />
          <SummaryCard
            label="Completed"
            value={String(stats.completed || 0)}
            accent="success"
          />
          <SummaryCard
            label="Failed"
            value={String(stats.failed || 0)}
            accent={stats.failed > 0 ? 'error' : 'default'}
          />
          <SummaryCard
            label="Total Volume"
            value={formatCurrency(stats.totalVolume || 0)}
            accent="info"
          />
        </div>
      )}

      {/* ─── Error Banner ─── */}
      {error && (
        <div className="flex items-center justify-between px-4 py-3 mb-6 rounded-lg border border-warning bg-warning-subtle">
          <p className="text-sm text-warning-emphasis">{error}</p>
          <button
            onClick={refetch}
            className="px-3 py-1 text-xs font-medium rounded-md bg-warning text-white hover:bg-warning-emphasis transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* ─── Filters ─── */}
      <div className="rounded-lg border border-edge bg-surface-subtle p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus min-w-[140px]"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="confirming">Confirming</option>
            <option value="crediting">Crediting</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={networkFilter}
            onChange={(e) => {
              setNetworkFilter(e.target.value);
              setPage(1);
            }}
            className="px-3 py-2 text-sm rounded-lg border border-edge bg-surface text-content focus:outline-none focus:border-edge-focus focus:ring-1 focus:ring-edge-focus min-w-[120px]"
          >
            <option value="">All Networks</option>
            <option value="testnet">Testnet</option>
            <option value="mainnet">Mainnet</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm font-medium text-content-muted hover:text-content transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ─── Orders Table ─── */}
      <div className="rounded-xl border border-edge bg-surface shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-2 border-edge border-t-brand-500 rounded-full animate-spin" />
            <p className="text-sm text-content-secondary">Loading orders…</p>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge bg-surface-subtle">
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Order ID</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">User</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">From</th>
                    <th className="px-5 py-3 text-right text-2xs uppercase tracking-wider font-semibold text-content-muted">CXXR Credited</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Wallet</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">TX Hash</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Status</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Network</th>
                    <th className="px-5 py-3 text-left text-2xs uppercase tracking-wider font-semibold text-content-muted">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge-subtle">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-surface-subtle/50 transition-colors">
                      <td className="px-5 py-3.5 text-xs font-mono text-content-secondary">
                        {order.id}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-content">
                            {order.userEmail || '—'}
                          </span>
                          <span className="text-2xs text-content-muted">
                            ID: {order.userId}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-content whitespace-nowrap">
                        {order.fromAmount} {order.fromToken}
                      </td>
                      <td className="px-5 py-3.5 text-sm font-semibold text-success text-right tabular-nums">
                        {formatCurrency(order.cxxrCredited)}
                      </td>
                      <td className="px-5 py-3.5 text-xs font-mono text-content-secondary" title={order.walletAddress}>
                        {truncateHash(order.walletAddress)}
                      </td>
                      <td className="px-5 py-3.5 text-xs font-mono" title={order.txHash}>
                        {order.txHash ? (
                          <a
                            href={getEtherscanUrl(order.txHash, order.networkMode)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-500 hover:text-brand-600 hover:underline transition-colors"
                          >
                            {truncateHash(order.txHash)} ↗
                          </a>
                        ) : (
                          <span className="text-content-subtle">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-5 py-3.5">
                        <NetworkBadge mode={order.networkMode} />
                      </td>
                      <td className="px-5 py-3.5 text-xs text-content-muted whitespace-nowrap">
                        {formatDate(order.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-edge bg-surface-subtle/50">
                <span className="text-xs text-content-muted">
                  Page {page} of {totalPages} ({total} total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-edge bg-surface text-content hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1.5 text-xs font-medium rounded-md border border-edge bg-surface text-content hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DexOnRampPage;
